import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, Image as ImgIcon, Inbox, Trophy, ArrowRight, Upload, Send, X, Pencil, Trash2, Link as LinkIcon, ExternalLink } from "lucide-react";

const EMPTY = { title: "", excerpt: "", body: "", cover_image: "", published: true, category: "racing" };
const MAX_UPLOAD_MB = 10;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const CATEGORIES = [
  { v: "racing", label: "Racing Team" },
  { v: "ktm", label: "KTM" },
  { v: "husqvarna", label: "Husqvarna" },
  { v: "gasgas", label: "GasGas Trial" },
  { v: "generale", label: "Generale" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({ news: 0, gallery: 0, requests: 0, newRequests: 0 });
  const [news, setNews] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = create mode; otherwise id
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [posterUrl, setPosterUrl] = useState("");
  const [posterUploading, setPosterUploading] = useState(false);
  const [posterProgress, setPosterProgress] = useState(0);
  const fileRef = useRef();
  const posterRef = useRef();
  const formRef = useRef();

  const loadStats = async () => {
    try {
      const [nR, gR, rR, pR] = await Promise.all([
        api.get("/admin/news"),
        api.get("/admin/gallery"),
        api.get("/admin/requests"),
        api.get("/settings/event-poster").catch(() => ({ data: { url: "" } })),
      ]);
      setStats({
        news: nR.data.length,
        gallery: gR.data.length,
        requests: rR.data.length,
        newRequests: rR.data.filter((x) => x.status === "new").length,
      });
      setNews(nR.data);
      setPosterUrl(pR.data?.url || "");
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStats(); }, []);

  const cards = [
    { to: "/admin/news", icon: Newspaper, label: "News pubblicate", value: stats.news, tid: "stat-news" },
    { to: "/admin/gallery", icon: ImgIcon, label: "Foto in gallery", value: stats.gallery, tid: "stat-gallery" },
    { to: "/admin/richieste", icon: Inbox, label: "Richieste totali", value: stats.requests, badge: stats.newRequests, tid: "stat-requests" },
    { to: "/admin/racing-story", icon: Trophy, label: "Storia Racing", value: "Modifica", tid: "stat-story" },
  ];

  const startEdit = (n) => {
    const bodyText = n.body || n.content || "";
    const excerpt = n.excerpt || bodyText.replace(/\s+/g, " ").slice(0, 220).trim();
    setEditingId(n.id);
    setForm({
      title: n.title || "",
      excerpt,
      body: bodyText,
      cover_image: n.cover_image || "",
      published: n.published !== false,
      category: n.category || "generale",
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type) && !file.type.startsWith("image/")) {
      toast.error("Formato non supportato. Usa JPG, PNG, WEBP o GIF.");
      return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_UPLOAD_MB) {
      toast.error(`File troppo grande (${sizeMB.toFixed(1)} MB). Massimo ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 120000,
      });
      setForm((f) => ({ ...f, cover_image: data.path }));
      toast.success(`Foto caricata (${(data.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload fallito");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 800);
    }
  };

  const save = async () => {
    if (uploading) { toast.error("Attendere fine upload foto."); return; }
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Compila titolo e corpo.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/news/${editingId}`, form);
        toast.success("News aggiornata!");
      } else {
        await api.post("/admin/news", form);
        toast.success("News pubblicata!");
      }
      resetForm();
      await loadStats();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Eliminare "${n.title}"?`)) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("News eliminata");
      if (editingId === n.id) resetForm();
      await loadStats();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore eliminazione");
    }
  };

  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (posterRef.current) posterRef.current.value = "";
    if (!file) return;
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_UPLOAD_MB) {
      toast.error(`File troppo grande (${sizeMB.toFixed(1)} MB). Massimo ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setPosterUploading(true);
    setPosterProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setPosterProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 120000,
      });
      await api.put("/admin/settings/event-poster", { value: data.path });
      setPosterUrl(data.path);
      toast.success("Locandina evento caricata");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload fallito");
    } finally {
      setPosterUploading(false);
      setTimeout(() => setPosterProgress(0), 800);
    }
  };

  const removePoster = async () => {
    if (!window.confirm("Rimuovere la locandina evento?")) return;
    try {
      await api.put("/admin/settings/event-poster", { value: "" });
      setPosterUrl("");
      toast.success("Locandina rimossa");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    }
  };

  const catLabel = (v) => CATEGORIES.find((c) => c.v === v)?.label || v;
  const dateStr = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-");

  const publicUrl = (n) => {
    // Compose the public URL based on category
    const base = window.location.origin;
    const cat = n.category || "racing";
    if (cat === "racing" || cat === "generale") return `${base}/moto/racing-team/news/${n.id}`;
    return `${base}/moto/${cat}/news/${n.id}`;
  };

  const copyLink = async (n) => {
    const url = publicUrl(n);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch {
      // Fallback: create a temporary input
      const tmp = document.createElement("input");
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand("copy"); toast.success("Link copiato"); }
      catch { toast.error(`Copia manualmente: ${url}`); }
      finally { document.body.removeChild(tmp); }
    }
  };

  return (
    <div>
      <span className="label-eyebrow text-gray-500">Benvenuto</span>
      <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Dashboard</h1>
      <p className="text-gray-600 mt-2">Panoramica del contenuto e delle richieste ricevute.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="card-flat p-6 flex flex-col gap-3" data-testid={c.tid}>
            <span className="text-[color:var(--nsa-orange)]"><c.icon size={22} /></span>
            <span className="label-eyebrow text-gray-500">{c.label}</span>
            <div className="flex items-baseline justify-between">
              <span className="font-display text-4xl">{c.value}</span>
              {c.badge ? <span className="text-xs bg-[color:var(--nsa-orange)] text-white px-2 py-1 rounded">{c.badge} nuove</span> : null}
            </div>
            <span className="text-xs text-gray-500 flex items-center gap-1 mt-auto">Vai <ArrowRight size={12} /></span>
          </Link>
        ))}
      </div>

      {/* Locandina Evento Off Road */}
      <div className="mt-14 border-2 border-dashed border-gray-300 p-6 md:p-8" data-testid="event-poster-manager">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className="text-[color:var(--nsa-orange)]"><ImgIcon size={22} /></span>
          <div className="flex-1">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Evento Off Road · 4 ottobre 2026</span>
            <h2 className="font-display uppercase text-2xl md:text-3xl">Locandina evento</h2>
            <p className="text-sm text-gray-600 mt-1">Formato consigliato <strong>4:5 verticale</strong> (es. 1080×1350). JPG/PNG/WEBP, max {MAX_UPLOAD_MB} MB.</p>
          </div>
          <Link to="/moto/evento-off-road" target="_blank" className="text-sm text-gray-600 hover:text-[color:var(--nsa-orange)] inline-flex items-center gap-1">
            Vedi pagina evento <ArrowRight size={12} />
          </Link>
        </div>

        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-40 md:w-52 aspect-[4/5] bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
            {posterUrl ? (
              <img src={mediaUrl(posterUrl)} alt="Locandina evento" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center px-3">
                <ImgIcon size={28} className="mx-auto text-gray-300" />
                <p className="text-xs text-gray-400 mt-2 uppercase tracking-widest">Nessuna locandina</p>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[220px] space-y-3">
            <input type="file" ref={posterRef} accept="image/jpeg,image/png,image/webp" onChange={uploadPoster} className="hidden" />
            <button
              type="button"
              onClick={() => posterRef.current?.click()}
              disabled={posterUploading}
              className="btn-orange label-eyebrow px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="poster-upload-btn"
            >
              <Upload size={14} /> {posterUploading ? `Caricamento… ${posterProgress}%` : (posterUrl ? "Sostituisci locandina" : "Carica locandina")}
            </button>
            {posterUploading && (
              <div className="h-2 bg-gray-200 overflow-hidden max-w-md">
                <div className="h-full bg-[color:var(--nsa-orange)] transition-all" style={{ width: `${posterProgress}%` }} />
              </div>
            )}
            {posterUrl && !posterUploading && (
              <button
                onClick={removePoster}
                className="border border-gray-300 hover:border-red-500 hover:text-red-600 px-4 py-2 label-eyebrow text-sm inline-flex items-center gap-2"
                data-testid="poster-remove-btn"
              >
                <X size={14} /> Rimuovi locandina
              </button>
            )}
            <p className="text-xs text-gray-500 leading-relaxed">
              La locandina apparirà sulla pagina pubblica <code className="bg-gray-100 px-1">/moto/evento-off-road</code>. Se non caricata, la sezione locandina non viene mostrata (il testo resta comunque visibile).
            </p>
          </div>
        </div>
      </div>

      {/* Quick publish / edit form */}
      <div ref={formRef} className="mt-14 border-2 border-[color:var(--nsa-orange)] p-6 md:p-8" data-testid="dashboard-quick-news">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span className="text-[color:var(--nsa-orange)]"><Send size={22} /></span>
          <div className="flex-1">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">{editingId ? "Modifica news" : "Pubblica rapidamente"}</span>
            <h2 className="font-display uppercase text-2xl md:text-3xl">{editingId ? "Aggiorna News" : "Nuova News"}</h2>
          </div>
          {editingId && (
            <button
              onClick={resetForm}
              className="text-sm text-gray-600 hover:text-red-600 inline-flex items-center gap-1"
              data-testid="cancel-edit-btn"
            >
              <X size={14} /> Annulla modifica
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Titolo*</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3"
                placeholder="Es. Vittoria al Mondiale Enduro"
                data-testid="quick-news-title"
              />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Estratto <span className="text-gray-400 normal-case tracking-normal">(opzionale — riassunto per la card)</span></label>
              <input
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3"
                placeholder="Poche righe di sintesi"
                data-testid="quick-news-excerpt"
              />
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Categoria*</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[color:var(--nsa-orange)]"
                data-testid="quick-news-category"
              >
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Cover image <span className="text-gray-400 normal-case tracking-normal">(max {MAX_UPLOAD_MB} MB)</span></label>
              <div className="flex items-start gap-4 flex-wrap">
                <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp,image/gif" onChange={upload} className="hidden" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="border border-gray-300 px-4 py-2 label-eyebrow inline-flex items-center gap-2 disabled:opacity-60 hover:border-gray-900 transition-colors"
                  data-testid="quick-news-upload"
                >
                  <Upload size={14} /> {uploading ? `Caricamento… ${uploadProgress}%` : (form.cover_image ? "Sostituisci" : "Carica")}
                </button>
                {form.cover_image && !uploading && (
                  <div className="relative">
                    <img src={mediaUrl(form.cover_image)} alt="cover" className="h-20 w-32 object-cover border border-gray-300" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, cover_image: "" })}
                      className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50 hover:text-red-600"
                      title="Rimuovi cover"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                {uploading && (
                  <div className="flex-1 min-w-[200px] max-w-md">
                    <div className="h-2 bg-gray-200 overflow-hidden">
                      <div className="h-full bg-[color:var(--nsa-orange)] transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">Corpo* <span className="text-gray-400 normal-case tracking-normal">(markdown: **grassetto**, ## titolo)</span></label>
            <textarea
              rows={12}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Scrivi qui il contenuto della news…"
              className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3 font-mono text-sm h-full min-h-[280px]"
              data-testid="quick-news-body"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <button
            onClick={save}
            disabled={saving || uploading}
            className="btn-orange label-eyebrow px-6 py-3 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="quick-news-publish"
          >
            <Send size={14} /> {saving ? "Salvataggio…" : (editingId ? "Aggiorna news" : "Pubblica ora")}
          </button>
          <button
            onClick={resetForm}
            disabled={saving}
            className="border border-gray-300 label-eyebrow px-6 py-3 disabled:opacity-60"
            data-testid="quick-news-reset"
          >
            {editingId ? "Chiudi" : "Svuota"}
          </button>
        </div>
      </div>

      {/* Existing news list */}
      <div className="mt-14">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="label-eyebrow text-gray-500">Elenco</span>
            <h2 className="font-display uppercase text-2xl md:text-3xl mt-1">Tutte le news</h2>
          </div>
          <span className="text-sm text-gray-500">{news.length} totali</span>
        </div>

        <div className="space-y-3">
          {news.length === 0 && (
            <p className="text-gray-500 italic">Nessuna news ancora pubblicata.</p>
          )}
          {news.map((n) => (
            <div
              key={n.id}
              className="border border-gray-200 hover:border-gray-400 transition-colors p-4 flex flex-col md:flex-row gap-4 items-start md:items-center"
              data-testid={`news-item-${n.id}`}
            >
              {n.cover_image ? (
                <img src={mediaUrl(n.cover_image)} alt="" className="w-full md:w-24 h-24 object-cover border border-gray-200 flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full md:w-24 h-24 bg-gray-100 flex items-center justify-center text-gray-400 flex-shrink-0"><ImgIcon size={20} /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-[10px] tracking-wider uppercase bg-[color:var(--nsa-orange)] text-white px-2 py-0.5">{catLabel(n.category)}</span>
                  <span className="text-xs text-gray-500">{dateStr(n.created_at)}</span>
                  {n.published === false && <span className="text-[10px] tracking-wider uppercase bg-gray-400 text-white px-2 py-0.5">Bozza</span>}
                </div>
                <div className="font-display uppercase text-base md:text-lg truncate">{n.title}</div>
                <div className="text-gray-600 text-sm truncate">{n.excerpt || (n.content || n.body || "").slice(0, 160)}</div>
              </div>
              <div className="flex gap-2 md:flex-col md:items-stretch flex-shrink-0 w-full md:w-auto">
                <button
                  onClick={() => copyLink(n)}
                  className="border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-2 text-sm inline-flex items-center gap-2 justify-center transition-colors"
                  data-testid={`copy-link-btn-${n.id}`}
                  title="Copia link pubblico (per Instagram, WhatsApp, ecc.)"
                >
                  <LinkIcon size={14} /> Copia link
                </button>
                <a
                  href={publicUrl(n).replace(window.location.origin, "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-gray-300 hover:border-gray-900 px-4 py-2 text-sm inline-flex items-center gap-2 justify-center transition-colors"
                  data-testid={`open-btn-${n.id}`}
                  title="Apri anteprima pubblica"
                >
                  <ExternalLink size={14} /> Anteprima
                </a>
                <button
                  onClick={() => startEdit(n)}
                  className="border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-2 text-sm inline-flex items-center gap-2 justify-center transition-colors"
                  data-testid={`edit-btn-${n.id}`}
                >
                  <Pencil size={14} /> Modifica
                </button>
                <button
                  onClick={() => remove(n)}
                  className="border border-gray-300 hover:border-red-500 hover:text-red-600 px-4 py-2 text-sm inline-flex items-center gap-2 justify-center transition-colors"
                  data-testid={`delete-btn-${n.id}`}
                >
                  <Trash2 size={14} /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
